import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
interface SlideLifecycleProps {
  onContinue: () => void;
}
export function SlideLifecycle({
  onContinue
}: SlideLifecycleProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 600); // axes
    const t2 = setTimeout(() => setStage(2), 1400); // main curve
    const t3 = setTimeout(() => setStage(3), 4000); // phase labels + bands
    const t4 = setTimeout(() => setStage(4), 5800); // potential area
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, []);
  const mainCurve = 'M 80 290 C 140 290 170 380 230 380 C 290 380 340 340 380 290 C 410 250 430 130 460 90 C 540 70 650 70 730 90 C 745 95 760 245 785 275 C 850 283 920 287 960 290';
  const potentialCurve = 'M 730 90 C 775 100 790 245 815 268 C 875 265 935 267 985 268';
  const potentialArea = 'M 730 90 C 775 100 790 245 815 268 C 875 265 935 267 985 268 L 960 290 C 920 287 850 283 785 275 C 760 245 745 95 730 90 Z';
  const phases = [{
    id: 'rd',
    label: 'R&D',
    sub: 'Investment & clinical trials',
    x: 230
  }, {
    id: 'launch',
    label: 'Launch',
    sub: 'Heavy marketing spend',
    x: 420
  }, {
    id: 'mature',
    label: 'Mature',
    sub: 'Peak revenue & share',
    x: 595
  }, {
    id: 'legacy',
    label: 'Legacy',
    sub: 'Loss of exclusivity',
    x: 845,
    accent: true
  }];
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-14 max-w-7xl mx-auto relative" data-id="element-557">
      {/* Snakker logo top-right */}
      <div className="absolute top-6 right-8 md:top-10 md:right-12 flex items-center gap-2 z-10" data-id="element-558">
        <div className="w-8 h-8 rounded-lg bg-teal-400 flex items-center justify-center" data-id="element-559">
          <div className="w-3.5 h-3.5 rounded-full bg-white" data-id="element-560" />
        </div>
        <span className="text-teal-400 font-bold text-base tracking-tight" data-id="element-561">
          snakker
        </span>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="text-center space-y-4" data-id="element-562">
        <h2 className="text-4xl md:text-5xl font-serif text-white" data-id="element-563">
          The Pharma Lifecycle
        </h2>
        <p className="text-xl text-white/60" data-id="element-564">
          Where the industry focuses vs. where the volume lives.
        </p>
      </motion.div>

      <div className="flex-1 relative flex items-center justify-center" data-id="element-565">
        <div className="w-full max-w-5xl relative" style={{
        aspectRatio: '2.2 / 1'
      }} data-id="element-566">
          <svg viewBox="0 0 1000 480" className="w-full h-full overflow-visible" data-id="element-567">
            <defs data-id="element-568">
              <pattern id="hatch" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)" data-id="element-569">
                <line x1="0" y1="0" x2="0" y2="8" stroke="#00D4AA" strokeWidth="1.5" strokeOpacity="0.4" data-id="element-570" />
              </pattern>
            </defs>

            {/* + and − axis labels */}
            <motion.text x="60" y="100" textAnchor="middle" className="fill-white/35 text-xs font-medium" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 1 ? 1 : 0
          }} data-id="element-571">
              +
            </motion.text>
            <motion.text x="60" y="385" textAnchor="middle" className="fill-white/35 text-xs font-medium" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 1 ? 1 : 0
          }} data-id="element-572">
              −
            </motion.text>

            <motion.text x="30" y="240" textAnchor="middle" className="fill-white/40 text-xs font-medium" style={{
            writingMode: 'vertical-rl',
            textOrientation: 'mixed'
          }} initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 1 ? 1 : 0
          }} data-id="element-573">
              Cumulative Value
            </motion.text>
            <motion.text x="520" y="455" textAnchor="middle" className="fill-white/40 text-xs font-medium" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 1 ? 1 : 0
          }} data-id="element-574">
              Time
            </motion.text>

            {/* Y axis */}
            <motion.line x1="80" y1="40" x2="80" y2="430" stroke="#3A5278" strokeWidth="2" initial={{
            pathLength: 0
          }} animate={{
            pathLength: stage >= 1 ? 1 : 0
          }} transition={{
            duration: 0.6
          }} data-id="element-575" />

            {/* Zero line */}
            <motion.line x1="80" y1="290" x2="970" y2="290" stroke="#3A5278" strokeWidth="2" initial={{
            pathLength: 0
          }} animate={{
            pathLength: stage >= 1 ? 1 : 0
          }} transition={{
            duration: 0.8
          }} data-id="element-576" />
            <motion.text x="975" y="295" className="fill-white/30 text-[10px] font-medium" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 1 ? 1 : 0
          }} transition={{
            delay: 0.4
          }} data-id="element-577">
              €0
            </motion.text>

            {/* Phase dividers */}
            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 3 ? 0.5 : 0
          }} transition={{
            duration: 0.5
          }} data-id="element-578">
              <line x1="380" y1="50" x2="380" y2="430" stroke="#3A5278" strokeWidth="1" strokeDasharray="4,4" data-id="element-579" />
              <line x1="460" y1="50" x2="460" y2="430" stroke="#3A5278" strokeWidth="1" strokeDasharray="4,4" data-id="element-580" />
              <line x1="730" y1="50" x2="730" y2="430" stroke="#3A5278" strokeWidth="1" strokeDasharray="4,4" data-id="element-581" />
            </motion.g>

            {/* Highlight band on Legacy phase */}
            <motion.rect x="730" y="50" width="230" height="380" fill="#00D4AA" fillOpacity="0.04" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 4 ? 1 : 0
          }} transition={{
            duration: 0.6
          }} data-id="element-582" />

            {/* Potential hatched area */}
            <motion.path d={potentialArea} fill="url(#hatch)" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 4 ? 1 : 0
          }} transition={{
            duration: 1.0
          }} data-id="element-583" />
            <motion.path d={potentialArea} fill="rgba(0, 212, 170, 0.08)" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 4 ? 1 : 0
          }} transition={{
            duration: 1.0
          }} data-id="element-584" />

            {/* Main curve */}
            <motion.path d={mainCurve} fill="none" stroke="#0f172a" strokeWidth="3" strokeOpacity="0.95" initial={{
            pathLength: 0
          }} animate={{
            pathLength: stage >= 2 ? 1 : 0
          }} transition={{
            duration: 2.6,
            ease: 'easeInOut'
          }} data-id="element-585" />

            {/* Potential dashed extension */}
            <motion.path d={potentialCurve} fill="none" stroke="#00D4AA" strokeWidth="3" strokeDasharray="8,4" initial={{
            pathLength: 0
          }} animate={{
            pathLength: stage >= 4 ? 1 : 0
          }} transition={{
            duration: 1.4,
            ease: 'easeInOut'
          }} data-id="element-586" />

            {/* Phase labels */}
            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 3 ? 1 : 0
          }} transition={{
            duration: 0.5
          }} data-id="element-587">
              {phases.map(p => <g key={p.label} data-id="element-588">
                  <text x={p.x} y="450" textAnchor="middle" className={`text-sm font-semibold ${p.accent ? 'fill-teal-400' : 'fill-white/65'}`} data-id="element-589">
                    {p.label}
                  </text>
                  <text x={p.x} y="468" textAnchor="middle" className={`text-xs ${p.accent ? 'fill-teal-300/60' : 'fill-white/35'}`} data-id="element-590">
                    {p.sub}
                  </text>
                </g>)}
            </motion.g>

            {/* POTENTIAL label */}
            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 4 ? 1 : 0
          }} transition={{
            duration: 0.6,
            delay: 0.6
          }} data-id="element-591">
              <line x1="845" y1="215" x2="900" y2="178" stroke="#00D4AA" strokeWidth="1" strokeOpacity="0.6" data-id="element-592" />
              <text x="908" y="170" className="fill-teal-400 text-sm font-bold tracking-wider" data-id="element-593">
                POTENTIAL
              </text>
              <text x="908" y="186" className="fill-teal-300/60 text-[10px]" data-id="element-594">
                Softening the cliff
              </text>
            </motion.g>

            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 3 ? 0.7 : 0
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} data-id="element-595">
              <text x="230" y="410" textAnchor="middle" className="fill-white/40 text-[10px] italic" data-id="element-596">
                Investment
              </text>
            </motion.g>

            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 3 ? 0.7 : 0
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} data-id="element-597">
              <text x="595" y="55" textAnchor="middle" className="fill-white/40 text-[10px] italic" data-id="element-598">
                Peak Revenue
              </text>
            </motion.g>
          </svg>

          {/* Floating insight card */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: stage >= 4 ? 1 : 0,
          y: stage >= 4 ? 0 : 20
        }} transition={{
          duration: 0.5,
          delay: 0.7
        }} className="absolute bottom-2 left-0 bg-white/95 border border-teal-400/30 shadow-lg p-4 rounded-xl max-w-sm backdrop-blur-sm" data-id="element-599">
            <h4 className="text-teal-400 font-semibold mb-1 text-sm" data-id="element-600">
              The Strategic Opportunity
            </h4>
            <p className="text-sm text-slate-700 leading-relaxed" data-id="element-601">
              In the Legacy phase, the dashed teal line shows what's possible
              with continuous optimization — slowing the decline and extending
              the revenue tail by even a few percentage points.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="absolute -left-20 top-0 bottom-0 flex flex-col justify-between text-xs text-white/40 font-medium py-4" data-id="element-602">
        <span data-id="element-603">Revenue</span>
        <span data-id="element-604">Investment</span>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: stage >= 5 ? 1 : 0,
      y: stage >= 5 ? 0 : 20
    }} className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-605">
        <div className="bg-navy-900/50 border border-navy-700 rounded-2xl p-6 space-y-3" data-id="element-606">
          <h3 className="text-teal-400 font-bold text-lg" data-id="element-607">The Focus</h3>
          <p className="text-white/70 leading-relaxed" data-id="element-608">
            Agencies and internal teams focus 90% of their effort on Launch and
            Mature phases, where budgets are highest.
          </p>
        </div>
        <div className="bg-purple-500/10 border border-purple-500/30 rounded-2xl p-6 space-y-3 relative overflow-hidden" data-id="element-609">
          <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/10 rounded-full blur-2xl -mr-16 -mt-16" data-id="element-610" />
          <h3 className="text-purple-400 font-bold text-lg" data-id="element-611">The Opportunity</h3>
          <p className="text-white/90 leading-relaxed relative z-10" data-id="element-612">
            Legacy brands generate massive volume but receive near-zero
            marketing support because traditional agency models are too
            expensive.
          </p>
        </div>
      </motion.div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: stage >= 6 ? 1 : 0
    }} className="mt-12 flex justify-center" data-id="element-613"></motion.div>
    </div>;
}